import { defineStore } from 'pinia';
import type {
    ICompatibilityGridData,
    ICompatibilityMappedGridHeaderItem,
    ICompatibilityStore,
    ICompatibilityTableData,
    ICompatibilityTableItem,
    ICompatibleModal,
} from '../../../shared/interfaces/compatibility-store.interface';
import { cloneDeep, concat, find, findIndex, get, set, toString } from 'lodash';

export const initialStateCompatibilityStore: ICompatibilityStore = {
    loading: false,
    dialogLoading: false,
    loadMoreLoading: false,
    compatibility: {
        metadata: {
            created_at: '',
            uri: '',
        },
        table: {
            machines: [],
            implements: [],
            current_page: 0,
            items_per_page: 0,
            total_items: 0,
            total_pages: 0,
        },
    },
    grid: {
        headers: [],
        columns: [],
    },
    mappedGrid: {
        headers: [],
        items: [],
    },
    filters: {
        model: '',
        category: '',
    },
    compatibleModalLoding: false,
    compatibleModal: {
        id: '',
        name: '',
        type: '',
        compatible_items: [],
        not_compatible_items: [],
        maybe_compatible_items: [],
        tableCompatibleItems: [],
    },
};

const useCompatibilityStore = defineStore('compatibility', {
    state: () => ({ ...initialStateCompatibilityStore }),
    getters: {
        selectCompatibilityLoading: (state): boolean => state.loading,
        selectCompatibilityDialogLoading: (state): boolean => state.dialogLoading,
        selectCompatibilityLoadMoreLoading: (state): boolean => state.loadMoreLoading,
        selectCompatibilityTableData: (state): ICompatibilityTableData => state.compatibility.table,
        selectCompatibilityGridDataHeaders: (state): string[] => state.grid.headers,
        selectCompatibilityGridDataColumns: (state): unknown[] => state.grid.columns,
        selectCompatibleModalLoading: (state): boolean => state.compatibleModalLoding,
        selectCompatibleModal: (state): ICompatibleModal => state.compatibleModal,
    },
    actions: {
        setCompatibilityModelFilterValue(model: string) {
            this.filters.model = model;
        },
        setCompatibilityCategoryFilterValue(category: string) {
            this.filters.category = category;
        },
        setCompatibilityMappedGridHeaders(headers: ICompatibilityMappedGridHeaderItem[]) {
            this.mappedGrid.headers = headers;
        },

        setCompatibilityMappedGridItems(items: any[]) {
            this.mappedGrid.items = items;
        },

        updateCompatibilityMappedGridItems(items: any[]) {
            const gridColumns: any[] = concat(cloneDeep(this.mappedGrid.items), cloneDeep(items));
            this.compatibility.table.current_page = this.compatibility.table.current_page + 1;
            this.mappedGrid.items = gridColumns;
        },
        setCompatibilityLoading(loading: boolean) {
            this.loading = loading;
        },
        setCompatibilityDialogLoading(loading: boolean) {
            this.dialogLoading = loading;
        },
        setCompatibilityLoadMoreLoading(loading: boolean) {
            this.loadMoreLoading = loading;
        },
        resetCompatibilityLoading() {
            this.loading = initialStateCompatibilityStore.loading;
        },
        setCompatibilityTable(tableData: ICompatibilityTableData) {
            this.compatibility.table = tableData;
        },
        resetCompatibilityTable() {
            this.compatibility.table = initialStateCompatibilityStore.compatibility.table;
        },
        setCompatibilityGridData(gridData: ICompatibilityGridData) {
            this.grid = gridData;
        },
        updateCompatibilityGridDataColumns(columns: unknown[]) {
            const gridColumns: unknown[] = concat(cloneDeep(this.grid.columns), cloneDeep(columns));
            this.compatibility.table.current_page = this.compatibility.table.current_page + 1;
            this.grid.columns = gridColumns;
        },
        ressetCompatibilityGridData() {
            this.grid = initialStateCompatibilityStore.grid;
        },
        updateCompatibilityMappedGridData(currentItem: { implement: string; machine: string }, value: string) {
            const columns = cloneDeep(this.mappedGrid.items);
            const itemForUpdate = find(columns, (x) => x.machine === currentItem.implement);
            set(itemForUpdate, currentItem.machine, value);
            this.mappedGrid.items = columns;
        },
        updateCompatibilityGridData(rowIndex: number, cellIndex: number, value: string) {
            const columns = cloneDeep(this.grid.columns);
            const rowForUpdate = get(columns, rowIndex);
            const cellKeyForUpdate = get(this.grid.headers, cellIndex);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            set(rowForUpdate, cellKeyForUpdate, value);
            set(columns, rowIndex, rowForUpdate);
            this.grid.columns = columns;
        },
        setCompatibleModalLoading(loading: boolean) {
            this.compatibleModalLoding = loading;
        },
        setCompatibleModalData(compatibleModal: ICompatibleModal) {
            this.compatibleModal = compatibleModal;
        },
        resetCompatibleModalData() {
            this.compatibleModal = initialStateCompatibilityStore.compatibleModal;
        },
        updateCompatibleModalData(item: ICompatibilityTableItem, value: string) {
            const tableCompatibleItems = cloneDeep(this.compatibleModal.tableCompatibleItems);
            const itemIndex: number = findIndex(tableCompatibleItems, (x) => x.id === item.id);
            const mappedItem: ICompatibilityTableItem | undefined = find(tableCompatibleItems, (x) => x.id === item.id);
            if (mappedItem !== undefined) {
                set(mappedItem, 'compatibility', toString(value));
            }
            set(tableCompatibleItems, itemIndex, mappedItem);
            this.compatibleModal.tableCompatibleItems = tableCompatibleItems;
        },
    },
});

export default useCompatibilityStore;
